/**
 * Console routes. Pages are lazy-loaded (one chunk per area). URL conventions shared with the API's
 * notification links: `/systems/:id?tab=`, `/library/:id`, `/sessions/:id?tab=…&fb=…`, `/settings?tab=`.
 */
import { lazy } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { AuthGate } from './auth/auth';
import { LoginPage } from './auth/LoginPage';
import { SetPasswordPage } from './auth/SetPasswordPage';
import { RouteError } from './components/RouteError';
import { NotFoundPage } from './pages/NotFoundPage';
import { AppShell } from './shell/AppShell';

const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const SystemsListPage = lazy(() => import('./pages/systems/SystemsListPage'));
const SystemDetailPage = lazy(() => import('./pages/systems/SystemDetailPage'));
const LibraryPage = lazy(() => import('./pages/library/LibraryPage'));
const CaseEditorPage = lazy(() => import('./pages/library/CaseEditorPage'));
const ImportWizardPage = lazy(() => import('./pages/library/ImportWizardPage'));
const SimulationPage = lazy(() => import('./pages/library/SimulationPage'));
const SessionsListPage = lazy(() => import('./pages/sessions/SessionsListPage'));
const SessionWizardPage = lazy(() => import('./pages/sessions/SessionWizardPage'));
const SessionDetailPage = lazy(() => import('./pages/sessions/SessionDetailPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const UsersPage = lazy(() => import('./pages/users/UsersPage'));
const GalleryPage = lazy(() => import('./pages/gallery/GalleryPage'));

export const routes: RouteObject[] = [
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },
  { path: '/set-password', element: <SetPasswordPage />, errorElement: <RouteError /> },
  {
    path: '/',
    errorElement: <RouteError />,
    element: (
      <AuthGate>
        <AppShell />
      </AuthGate>
    ),
    children: [
      {
        // Page errors render inside the shell so navigation keeps working.
        errorElement: <RouteError inline />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'systems', element: <SystemsListPage /> },
          { path: 'systems/:id', element: <SystemDetailPage /> },
          { path: 'library', element: <LibraryPage /> },
          { path: 'library/new', element: <CaseEditorPage /> },
          { path: 'library/import', element: <ImportWizardPage /> },
          { path: 'library/:id', element: <CaseEditorPage /> },
          { path: 'library/:id/simulate', element: <SimulationPage /> },
          { path: 'sessions', element: <SessionsListPage /> },
          { path: 'sessions/new', element: <SessionWizardPage /> },
          { path: 'sessions/:id', element: <SessionDetailPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'users', element: <UsersPage /> },
          { path: 'gallery', element: <GalleryPage /> },
          { path: 'dashboard', element: <Navigate to="/" replace /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
